<template>
  <div id="admin">
    <div class="admin-header">
      <div class="left">
        <div class="circle-icon" @click="goHome()">
          <b-icon icon="arrow-left" variant="primary"></b-icon>
        </div>
      </div>
      <div class="middle">
        <img class="logo-clementine" src="@/assets/images/logo-clementine.png" @click="goHome()" />
        <span class="admin-title">Administration</span>
      </div>
        <div class="right">
        <div class="circle-icon" @click="goHome()"><b-icon icon="house-fill" variant="primary"></b-icon></div>
        </div>
    </div>

    <div class="admin-container">
      
      <!-- Loading state -->
      <div v-if="isLoading" class="loading-overlay">
        <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
        <p class="mt-3">Chargement en cours...</p>
      </div>
      
      <div v-else>
      
      <!-- Top Row: Favorites and Categories -->
      <div class="top-row">
        
        <!-- Favorites Management -->
        <div class="admin-section favorites-section">
          <div class="section-header">
            <h3>Obsessions du moment</h3>
            <b-button size="sm" variant="primary" @click="showAddFavoriteModal = true">
              <b-icon icon="plus"></b-icon> Ajouter
            </b-button>
          </div>
          
          <div class="favorites-grid">
            <draggable 
              v-model="favoriteRecipes" 
              @end="updateFavoritesOrder"
              class="draggable-grid"
            >
              <div 
                v-for="recipe in favoriteRecipes" 
                :key="recipe.id"
                class="favorite-card"
              >
                <img :src="getImagePath(recipe.photoName, recipe.photoUrl)" :alt="recipe.name" class="favorite-image" />
                <div class="favorite-overlay">
                  <h6>{{ recipe.name }}</h6>
                  <b-button 
                    size="sm" 
                    variant="primary" 
                    @click="removeFavorite(recipe.id)"
                  >
                    <b-icon icon="trash"></b-icon>
                  </b-button>
                </div>
              </div>
            </draggable>
            
            <div v-if="favoriteRecipes.length === 0" class="empty-state">
              <p>Aucune recette favorite</p>
              <small class="text-muted">
                Ajoutez des recettes en cliquant sur "Ajouter" ci-dessus
              </small>
            </div>
          </div>
        </div>

        <!-- Categories Management -->
        <div class="admin-section categories-section">
          <div class="section-header">
            <h3>Catégories</h3>
            <b-button size="sm" variant="primary" @click="showAddCategoryModal = true">
              <b-icon icon="plus"></b-icon> Ajouter
            </b-button>
          </div>
          
          <div class="categories-list">
            <div 
              v-for="category in categories" 
              :key="category.id"
              class="category-item"
            >
              <span class="category-name">{{ category.nom }}</span>
              <b-button-group size="sm" class="category-actions">
                <b-button 
                  size="sm" 
                  variant="primary"
                  @click="editCategory(category)"
                >
                  <b-icon icon="pencil"></b-icon>
                </b-button>
                <b-button 
                  size="sm" 
                  variant="primary"
                  @click="confirmDeleteCategory(category)"
                >
                  <b-icon icon="trash"></b-icon>
                </b-button>
              </b-button-group>
            </div>
          </div>
        </div>
      </div>

      <!-- Recipes Management -->
      <div class="admin-section recipes-section">
        <div class="section-header">
          <h3>Recettes</h3>
          <div class="recipes-controls">
            <b-form-select v-model="sortBy" @change="sortRecipes" size="sm">
              <option value="date">Trier par date</option>
              <option value="name">Trier par nom</option>
              <option value="category">Trier par catégorie</option>
            </b-form-select>
            <b-button size="sm" variant="primary" @click="showAddRecipeModal = true" class="flex">
              <b-icon icon="plus"></b-icon> Ajouter
            </b-button>
          </div>
        </div>

        <div class="recipes-table">
          <!-- Desktop Table View -->
          <b-table 
            :items="sortedRecipes" 
            :fields="recipeFields"
            striped 
            hover
            responsive
            class="desktop-table"
          >
            <template #cell(photoName)="data">
              <img :src="getImagePath(data.value, data.item.photoUrl)" class="recipe-thumb" />
            </template>
            
            <template #cell(categories)="data">
              <span 
                v-for="categoryId in data.value" 
                :key="categoryId"
                class="category-badge"
              >
                {{ getCategoryName(categoryId) }}
              </span>
            </template>
            
            <template #cell(datePublication)="data">
              {{ formatDate(data.value) }}
            </template>
            
            <template #cell(actions)="data">
              <b-button-group size="sm" class="category-actions">
                <b-button variant="primary" @click="editRecipe(data.item)">
                  <b-icon icon="pencil"></b-icon>
                </b-button>
                <b-button variant="primary" @click="confirmDeleteRecipe(data.item)">
                  <b-icon icon="trash"></b-icon>
                </b-button>
              </b-button-group>
            </template>
          </b-table>
          
          <!-- Mobile Cards View -->
          <div class="mobile-cards">
            <div 
              v-for="recipe in sortedRecipes" 
              :key="recipe.id"
              class="recipe-mobile-card"
            >
              <div class="mobile-card-header">
                <img :src="getImagePath(recipe.photoName, recipe.photoUrl)" class="mobile-recipe-thumb" />
                <div class="mobile-card-info">
                  <h6 class="mobile-recipe-name">{{ recipe.name }}</h6>
                  <small class="mobile-recipe-date">{{ formatDate(recipe.datePublication) }}</small>
                </div>
                <div class="mobile-actions">
                  <b-button size="sm" variant="outline-primary" @click="editRecipe(recipe)">
                    <b-icon icon="pencil"></b-icon>
                  </b-button>
                  <b-button size="sm" variant="outline-danger" @click="confirmDeleteRecipe(recipe)">
                    <b-icon icon="trash"></b-icon>
                  </b-button>
                </div>
              </div>
              
              <div class="mobile-categories" v-if="recipe.categories.length > 0">
                <span 
                  v-for="categoryId in recipe.categories" 
                  :key="categoryId"
                  class="category-badge mobile-badge"
                >
                  {{ getCategoryName(categoryId) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Favorite Modal -->
    <b-modal 
      v-model="showAddFavoriteModal" 
      title="Ajouter aux favoris"
      @ok="addToFavorites"
    >
      <b-form-select v-model="selectedRecipeToFavorite">
        <option :value="null">Sélectionner une recette...</option>
        <option 
          v-for="recipe in availableRecipesForFavorites" 
          :key="recipe.id"
          :value="recipe.id"
        >
          {{ recipe.name }}
        </option>
      </b-form-select>
    </b-modal>

    <!-- Add/Edit Category Modal -->
    <b-modal 
      v-model="showAddCategoryModal" 
      :title="editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'"
      @ok="saveCategory"
    >
      <b-form-input 
        v-model="categoryForm.nom" 
        placeholder="Nom de la catégorie"
      ></b-form-input>
    </b-modal>

    <!-- Add/Edit Recipe Modal -->
    <b-modal 
      v-model="showAddRecipeModal" 
      :title="editingRecipe ? 'Modifier la recette' : 'Nouvelle recette'"
      size="xl"
      body-class="recipe-modal-body"
      modal-class="recipe-modal"
      @ok="saveRecipe"
      @show="resetPreviewTab"
    >
      <b-tabs v-model="activeRecipeTab" class="recipe-tabs">
        <!-- Onglet Édition -->
        <b-tab title="Édition" active>
          <b-form class="mt-3">
            <b-row>
              <b-col md="6">
                <b-form-group label="Nom de la recette">
                  <b-form-input v-model="recipeForm.name" @input="updatePreview"></b-form-input>
                </b-form-group>
                
                <b-form-group label="Date de publication">
                  <b-form-input type="date" v-model="recipeForm.datePublication" @input="updatePreview"></b-form-input>
                </b-form-group>
                
                <b-form-group label="Catégories">
                  <b-form-checkbox-group v-model="recipeForm.categories" @input="updatePreview">
                    <b-form-checkbox 
                      v-for="category in categories" 
                      :key="category.id"
                      :value="category.id"
                    >
                      {{ category.nom }}
                    </b-form-checkbox>
                  </b-form-checkbox-group>
                </b-form-group>
                
                <b-form-group label="Photo">
                  <b-form-file 
                    ref="photoFileInput"
                    v-model="recipeForm.photo" 
                    accept="image/*"
                    @change="onPhotoSelect"
                    placeholder="Aucun fichier sélectionné"
                    browse-text="Parcourir"
                    drop-placeholder="Déposer le fichier ici..."
                  ></b-form-file>
                  <div v-if="recipeForm.photoPreview" class="mt-2">
                    <img :src="recipeForm.photoPreview" class="photo-preview" />
                    <div class="photo-actions mt-2">
                      <b-button size="sm" variant="outline-secondary" @click="clearPhoto">
                        <b-icon icon="trash"></b-icon> Supprimer la photo
                      </b-button>
                    </div>
                  </div>
                </b-form-group>
              </b-col>
              
              <b-col md="6">
                <b-form-group label="Contenu de la recette">
                  <div class="rich-editor">
                    <div class="editor-toolbar">
                      <b-button-group size="sm">
                        <b-button @click="formatText('bold')"><strong>B</strong></b-button>
                        <b-button @click="formatText('italic')"><em>I</em></b-button>
                        <b-button @click="formatText('underline')"><u>U</u></b-button>
                        <b-button @click="createLink()">🔗 Lien</b-button>
                        <b-button @click="insertList('ul')">• Liste</b-button>
                        <b-button @click="insertList('ol')">1. Liste</b-button>
                      </b-button-group>
                    </div>
                    <div 
                      ref="contentEditor" 
                      class="content-editor"
                      contenteditable="true"
                      @input="updateContent"
                      @keyup="updateContent"
                      @paste="handlePaste"
                      v-html="recipeForm.content"
                    ></div>
                  </div>
                </b-form-group>
              </b-col>
            </b-row>
          </b-form>
        </b-tab>
        
        <!-- Onglet Aperçu -->
        <b-tab title="Aperçu">
          <div class="recipe-preview mt-3">
            <div class="preview-header">
              <h2 class="recipe-title">{{ recipeForm.name || 'Nom de la recette' }}</h2>
              <div class="recipe-meta">
                <span class="publication-date">
                  {{ formatDate(recipeForm.datePublication) }}
                </span>
                <div class="recipe-categories mt-2">
                  <span 
                    v-for="categoryId in recipeForm.categories" 
                    :key="categoryId"
                    class="category-badge preview-badge"
                  >
                    {{ getCategoryName(categoryId) }}
                  </span>
                </div>
              </div>
            </div>
            
            <div v-if="recipeForm.photoPreview" class="recipe-photo-container">
              <img :src="recipeForm.photoPreview" class="recipe-photo-preview" />
            </div>
            
            <div class="recipe-content-preview" v-html="recipeForm.content || '<p><em>Le contenu de la recette apparaîtra ici...</em></p>'">
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </b-modal>

    <!-- Delete Confirmation Modals -->
    <b-modal 
      v-model="showDeleteCategoryModal" 
      title="Confirmer la suppression"
      @ok="deleteCategory"
    >
      <p>Êtes-vous sûr de vouloir supprimer la catégorie <strong>{{ categoryToDelete?.nom }}</strong> ?</p>
      <p class="text-muted">Cette action est irréversible.</p>
    </b-modal>

    <b-modal 
      v-model="showDeleteRecipeModal" 
      title="Confirmer la suppression"
      @ok="deleteRecipe"
    >
      <p>Êtes-vous sûr de vouloir supprimer la recette <strong>{{ recipeToDelete?.name }}</strong> ?</p>
      <p class="text-muted">Cette action est irréversible.</p>
    </b-modal>
    
    <!-- Modal pour créer un lien -->
    <b-modal 
      v-model="showLinkModal" 
      title="Créer un lien"
      @ok="confirmCreateLink"
      @cancel="cancelCreateLink"
    >
      <p>Texte sélectionné : <strong>{{ selectedTextForLink }}</strong></p>
      <b-form-group label="URL du lien :">
        <b-form-input 
          v-model="linkUrl" 
          placeholder="https://exemple.com"
          type="url"
          ref="linkUrlInput"
        ></b-form-input>
      </b-form-group>
    </b-modal>
    
    </div> <!-- End of v-else -->
    
    </div> <!-- End of admin-container -->
</template>

<script>
import FirebaseDataService from '../services/FirebaseDataService.js'
import draggable from 'vuedraggable'

export default {
  name: 'Admin',
  components: {
    draggable
  },
  data() {
    return {
      // Data sources
      recipes: [],
      categories: [],
      favoriteRecipes: [],
      
      // UI State
      sortBy: 'date',
      showHelp: false,
      showAddFavoriteModal: false,
      showAddCategoryModal: false,
      showAddRecipeModal: false,
      showDeleteCategoryModal: false,
      showDeleteRecipeModal: false,
      showLinkModal: false,
      isLoading: false,
      activeRecipeTab: 0, // 0 = Édition, 1 = Aperçu
      
      // Forms
      selectedRecipeToFavorite: null,
      categoryForm: { nom: '' },
      recipeForm: {
        name: '',
        datePublication: new Date().toISOString().split('T')[0],
        categories: [],
        content: '',
        photo: null,
        photoPreview: null
      },
      
      // Link creation
      linkUrl: '',
      selectedTextForLink: '',
      savedSelection: null,
      
      // Editing state
      editingCategory: null,
      editingRecipe: null,
      categoryToDelete: null,
      recipeToDelete: null,
      
      // Table configuration
      recipeFields: [
        { key: 'photoName', label: 'Photo' },
        { key: 'name', label: 'Nom', sortable: true },
        { key: 'datePublication', label: 'Date', sortable: true },
        { key: 'categories', label: 'Catégories' },
        { key: 'actions', label: 'Actions' }
      ]
    };
  },
  computed: {
    sortedRecipes() {
      const sorted = [...this.recipes];
      
      switch (this.sortBy) {
        case 'name':
          return sorted.sort((a, b) => a.name.localeCompare(b.name));
        case 'category':
          return sorted.sort((a, b) => {
            const aCat = this.getCategoryName(a.categories[0] || '');
            const bCat = this.getCategoryName(b.categories[0] || '');
            return aCat.localeCompare(bCat);
          });
        case 'date':
        default:
          return sorted.sort((a, b) => new Date(b.datePublication) - new Date(a.datePublication));
      }
    },
    availableRecipesForFavorites() {
      const favoriteIds = this.favoriteRecipes.map(r => r.id);
      return this.recipes.filter(recipe => !favoriteIds.includes(recipe.id));
    }
  },
  async mounted() {
    console.log('Admin component mounted');
    
    // Load PWA configuration for admin page only
    this.loadPWAConfiguration();
    
    try {
      await this.loadData();
      console.log('Data loaded successfully:', {
        recipes: this.recipes.length,
        categories: this.categories.length,
        favorites: this.favoriteRecipes.length
      });
    } catch (error) {
      console.error('Error in mounted:', error);
    }
  },
  methods: {
    // ================= PWA CONFIGURATION =================
    loadPWAConfiguration() {
      // Create custom icon with background color first
      this.createCustomIcon().then(iconDataUrl => {
        // Add PWA manifest
        const manifest = document.createElement('link');
        manifest.rel = 'manifest';
        manifest.href = process.env.BASE_URL + 'manifest.json';
        document.head.appendChild(manifest);
        
        // Add iOS PWA meta tags
        const metaTags = [
          { name: 'apple-mobile-web-app-capable', content: 'yes' },
          { name: 'apple-mobile-web-app-status-bar-style', content: 'default' },
          { name: 'apple-mobile-web-app-title', content: 'BlogAdmin' },
          { name: 'theme-color', content: '#a13b6e' },
          { name: 'msapplication-TileColor', content: '#a13b6e' }
        ];
        
        metaTags.forEach(tag => {
          const meta = document.createElement('meta');
          meta.name = tag.name;
          meta.content = tag.content;
          document.head.appendChild(meta);
        });
        
        // Add iOS touch icons with custom icon
        const iconSizes = ['152x152', '180x180', '167x167'];
        iconSizes.forEach(size => {
          const icon = document.createElement('link');
          icon.rel = 'apple-touch-icon';
          icon.sizes = size;
          icon.href = iconDataUrl;
          document.head.appendChild(icon);
        });
        
        // Add default apple touch icon
        const defaultIcon = document.createElement('link');
        defaultIcon.rel = 'apple-touch-icon';
        defaultIcon.href = iconDataUrl;
        document.head.appendChild(defaultIcon);
      });
    },
    
    createCustomIcon(size = 192) {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        
        // Fill background with secondary color
        ctx.fillStyle = '#e4b5cd';
        ctx.fillRect(0, 0, size, size);
        
        // Load and draw the logo, smaller and centered
        const img = new Image();
        img.onload = () => {
          const logoSize = size * 0.6; // Make logo 60% of canvas size
          const x = (size - logoSize) / 2;
          const y = (size - logoSize) / 2;
          
          ctx.drawImage(img, x, y, logoSize, logoSize);
          resolve(canvas.toDataURL('image/png'));
        };
        img.src = process.env.BASE_URL + 'logo-clementine.png';
      });
    },
    
    // ================= DATA LOADING =================
    async loadData() {
      this.isLoading = true;
      try {
        // S'assurer que le service est initialisé
        await FirebaseDataService.initializeData();
        
        // Récupérer toutes les données depuis Firebase
        this.recipes = await FirebaseDataService.getRecipes();
        this.categories = await FirebaseDataService.getCategories();
        this.favoriteRecipes = await FirebaseDataService.getFavoriteRecipes();
        
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        this.showError('Erreur lors du chargement des données');
      } finally {
        this.isLoading = false;
      }
    },
    
    // ================= NAVIGATION =================
    goHome() {
      this.$router.push('/');
    },
    
    // ================= UTILITY METHODS =================
    getImagePath(photoName, photoUrl = null) {
      if (!photoName) {
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y4ZjlmYSIvPgogIDx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTRweCIgZmlsbD0iIzk5OWIzMSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSI+UGFzIGRlIHBob3RvPC90ZXh0Pgo8L3N2Zz4=';
      }
      
      // If there's a Firebase URL (new uploads), use it directly
      if (photoUrl && photoUrl.includes('firebase')) {
        return photoUrl;
      }
      
      // Otherwise, try to load from local assets (existing images)
      try {
        return require(`@/assets/images/${photoName}`);
      } catch (error) {
        console.warn(`Local image not found: ${photoName}, falling back`, error);
        // If local image doesn't exist and we have a URL, try that
        if (photoUrl) {
          return photoUrl;
        }
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y4ZjlmYSIvPgogIDx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTRweCIgZmlsbD0iIzk5OWIzMSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSI+UGhvdG8gbm9uIGRpc3BvbmlibGU8L3RleHQ+Cjwvc3ZnPg==';
      }
    },
    
    getCategoryName(categoryId) {
      const category = this.categories.find(c => c.id === categoryId);
      return category ? category.nom : '';
    },
    
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('fr-CA');
    },
    
    showSuccess(message) {
      this.$bvToast.toast(message, {
        title: 'Succès',
        variant: 'success'
      });
    },
    
    showError(message) {
      this.$bvToast.toast(message, {
        title: 'Erreur',
        variant: 'danger'
      });
    },
    
    // ================= FAVORITES MANAGEMENT =================
    async updateFavoritesOrder() {
      try {
        await FirebaseDataService.reorderFavorites(this.favoriteRecipes);
        this.showSuccess('Ordre des favoris mis à jour');
      } catch (error) {
        this.showError('Erreur lors de la mise à jour de l\'ordre');
        console.error(error);
      }
    },
    
    async addToFavorites() {
      if (this.selectedRecipeToFavorite) {
        try {
          const success = await FirebaseDataService.addFavoriteRecipe(this.selectedRecipeToFavorite);
          if (success) {
            await this.loadData();
            this.selectedRecipeToFavorite = null;
            this.showSuccess('Recette ajoutée aux favoris');
          } else {
            this.showError('Impossible d\'ajouter la recette aux favoris');
          }
        } catch (error) {
          this.showError('Erreur lors de l\'ajout aux favoris');
          console.error(error);
        }
      }
    },
    
    async removeFavorite(recipeId) {
      try {
        const success = await FirebaseDataService.removeFavoriteRecipe(recipeId);
        if (success) {
          await this.loadData();
          this.showSuccess('Recette retirée des favoris');
        }
      } catch (error) {
        this.showError('Erreur lors de la suppression du favori');
        console.error(error);
      }
    },
    
    // ================= CATEGORIES MANAGEMENT =================
    editCategory(category) {
      this.editingCategory = category;
      this.categoryForm.nom = category.nom;
      this.showAddCategoryModal = true;
    },
    
    confirmDeleteCategory(category) {
      this.categoryToDelete = category;
      this.showDeleteCategoryModal = true;
    },
    
    async saveCategory() {
      if (!this.categoryForm.nom.trim()) {
        this.showError('Le nom de la catégorie est requis');
        return false;
      }
      
      try {
        if (this.editingCategory) {
          // Modifier existante
          await FirebaseDataService.updateCategory(this.editingCategory.id, this.categoryForm);
          this.showSuccess('Catégorie modifiée avec succès');
        } else {
          // Ajouter nouvelle
          await FirebaseDataService.addCategory(this.categoryForm);
          this.showSuccess('Catégorie ajoutée avec succès');
        }
        
        await this.loadData();
        this.resetCategoryForm();
        return true;
      } catch (error) {
        this.showError('Erreur lors de la sauvegarde de la catégorie');
        console.error(error);
        return false;
      }
    },
    
    async deleteCategory() {
      if (this.categoryToDelete) {
        try {
          const success = await FirebaseDataService.deleteCategory(this.categoryToDelete.id);
          if (success) {
            await this.loadData();
            this.showSuccess('Catégorie supprimée avec succès');
          }
          this.categoryToDelete = null;
        } catch (error) {
          this.showError('Erreur lors de la suppression de la catégorie');
          console.error(error);
        }
      }
    },
    
    resetCategoryForm() {
      this.editingCategory = null;
      this.categoryForm.nom = '';
      this.showAddCategoryModal = false;
    },
    
    // ================= RECIPES MANAGEMENT =================
    sortRecipes() {
      // Computed property handles this automatically
    },
    
    editRecipe(recipe) {
      this.editingRecipe = recipe;
      this.recipeForm = {
        name: recipe.name,
        datePublication: recipe.datePublication.split('T')[0],
        categories: [...recipe.categories],
        content: recipe.content,
        photo: null, // No new photo selected initially
        photoPreview: recipe.photoName ? this.getImagePath(recipe.photoName, recipe.photoUrl) : null
      };
      this.showAddRecipeModal = true;
    },
    
    confirmDeleteRecipe(recipe) {
      this.recipeToDelete = recipe;
      this.showDeleteRecipeModal = true;
    },
    
    async saveRecipe() {
      if (!this.recipeForm.name.trim()) {
        this.showError('Le nom de la recette est requis');
        return false;
      }
      
      try {
        this.isLoading = true;
        
        const recipeData = {
          name: this.recipeForm.name,
          datePublication: this.recipeForm.datePublication,
          categories: this.recipeForm.categories,
          content: this.recipeForm.content
        };
        
        // Upload photo if a new one is selected
        if (this.recipeForm.photo) {
          console.log('Uploading photo...');
          const timestamp = Date.now();
          const fileName = `recipe-${timestamp}-${this.recipeForm.photo.name}`;
          
          const uploadResult = await FirebaseDataService.uploadPhoto(this.recipeForm.photo, fileName);
          recipeData.photoName = uploadResult.fileName;
          recipeData.photoUrl = uploadResult.url;
          
          console.log('Photo uploaded successfully:', uploadResult);
        } else if (this.editingRecipe && this.editingRecipe.photoName) {
          // Keep existing photo if editing and no new photo selected
          recipeData.photoName = this.editingRecipe.photoName;
          recipeData.photoUrl = this.editingRecipe.photoUrl;
        }
        
        if (this.editingRecipe) {
          // Modifier existante
          await FirebaseDataService.updateRecipe(this.editingRecipe.id, recipeData);
          this.showSuccess('Recette modifiée avec succès');
        } else {
          // Ajouter nouvelle
          await FirebaseDataService.addRecipe(recipeData);
          this.showSuccess('Recette ajoutée avec succès');
        }
        
        await this.loadData();
        this.resetRecipeForm();
        return true;
      } catch (error) {
        this.showError('Erreur lors de la sauvegarde de la recette: ' + error.message);
        console.error(error);
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    
    async deleteRecipe() {
      if (this.recipeToDelete) {
        try {
          const success = await FirebaseDataService.deleteRecipe(this.recipeToDelete.id);
          if (success) {
            await this.loadData();
            this.showSuccess('Recette supprimée avec succès');
          }
          this.recipeToDelete = null;
        } catch (error) {
          this.showError('Erreur lors de la suppression de la recette');
          console.error(error);
        }
      }
    },
    
    resetRecipeForm() {
      this.editingRecipe = null;
      this.recipeForm = {
        name: '',
        datePublication: new Date().toISOString().split('T')[0],
        categories: [],
        content: '',
        photo: null,
        photoPreview: null
      };
      this.activeRecipeTab = 0; // Remet l'onglet sur "Édition"
      this.showAddRecipeModal = false;
    },
    
    // Clear photo selection
    clearPhoto() {
      this.recipeForm.photo = null;
      this.recipeForm.photoPreview = null;
      // Reset the file input
      const fileInput = this.$refs.photoFileInput;
      if (fileInput) {
        fileInput.reset();
      }
      this.updatePreview();
    },
    
    // Photo handling
    onPhotoSelect(file) {
      console.log('Photo selected:', file);
      
      // Handle both cases: direct file or event object
      let selectedFile = file;
      if (file && file.target && file.target.files) {
        // It's an event object
        selectedFile = file.target.files[0];
      } else if (!file || (file instanceof Event)) {
        // It's an event or null, try to get file from the component's value
        const fileInput = this.$refs.photoFileInput;
        if (fileInput && fileInput.$el) {
          const input = fileInput.$el.querySelector('input[type="file"]');
          selectedFile = input ? input.files[0] : null;
        } else {
          selectedFile = null;
        }
      }
      
      console.log('Actual file:', selectedFile);
      
      if (selectedFile && selectedFile instanceof File) {
        this.recipeForm.photo = selectedFile; // Store the actual file
        const reader = new FileReader();
        reader.onload = (e) => {
          this.recipeForm.photoPreview = e.target.result;
          this.updatePreview();
        };
        reader.readAsDataURL(selectedFile);
      } else {
        console.warn('No valid file selected');
      }
    },
    
    // Rich text editor methods
    formatText(command) {
      document.execCommand(command, false, null);
      this.$refs.contentEditor.focus();
      // Mettre à jour le contenu après formatage
      this.$nextTick(() => {
        this.updateContent();
      });
    },
    
    insertList(type) {
      document.execCommand(type === 'ul' ? 'insertUnorderedList' : 'insertOrderedList', false, null);
      this.$refs.contentEditor.focus();
      // Mettre à jour le contenu après insertion de liste
      this.$nextTick(() => {
        this.updateContent();
      });
    },
    
    createLink() {
      const selection = window.getSelection();
      if (!selection.toString()) {
        this.$bvToast.toast('Veuillez sélectionner du texte pour créer un lien', {
          title: 'Attention',
          variant: 'warning',
          solid: true
        });
        return;
      }
      
      // Sauvegarder la sélection
      this.savedSelection = selection.getRangeAt(0);
      this.selectedTextForLink = selection.toString();
      this.linkUrl = 'https://';
      this.showLinkModal = true;
      
      // Focus sur le champ URL après ouverture de la modal
      this.$nextTick(() => {
        if (this.$refs.linkUrlInput) {
          this.$refs.linkUrlInput.focus();
        }
      });
    },
    
    confirmCreateLink() {
      if (!this.linkUrl || !this.linkUrl.trim()) {
        return;
      }
      
      // Restaurer la sélection et créer le lien
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(this.savedSelection);
      
      document.execCommand('createLink', false, this.linkUrl.trim());
      this.$refs.contentEditor.focus();
      
      this.updateContent();
      this.resetLinkForm();
    },
    
    cancelCreateLink() {
      this.resetLinkForm();
    },
    
    resetLinkForm() {
      this.linkUrl = '';
      this.selectedTextForLink = '';
      this.savedSelection = null;
      this.showLinkModal = false;
    },
    
    updateContent() {
      this.recipeForm.content = this.$refs.contentEditor.innerHTML;
      // Forcer la réactivité de Vue pour l'aperçu
      this.$forceUpdate();
    },
    
    handlePaste() {
      // Délai pour que le contenu soit inséré avant la mise à jour
      this.$nextTick(() => {
        this.updateContent();
      });
    },
    
    // Preview methods
    updatePreview() {
      // Force Vue à détecter les changements pour l'aperçu
      this.$forceUpdate();
    },
    
    resetPreviewTab() {
      this.activeRecipeTab = 0; // Repasse automatiquement sur l'onglet d'édition
    }
  }
}
</script>

<style scoped>
#admin {
  background: #f8f9fa;
  min-height: 100vh;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  padding-top: env(safe-area-inset-top); /* iOS safe area support */
  display: flex;
  flex-direction: column;
}

/* ============= HEADER ============= */
@import url('https://fonts.googleapis.com/css2?family=Ubuntu:wght@400;700&display=swap');

.admin-header {
  text-align: center;
  font-family: 'Ubuntu', sans-serif;
  padding: 0.5rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background-color: var(--secondary);
  color: #e6e6e6;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
  width: 100%;
}

/* Mobile header fixes */
@media (max-width: 768px) {
  .admin-header {
    width: 100%;
    padding: 1rem 0.5rem;
    padding-top: calc(1rem + env(safe-area-inset-top));
    min-height: 60px;
  }
  
  .admin-container {
    padding-top: calc(80px + env(safe-area-inset-top)); /* Account for fixed header */
  }
}

.logo-clementine {
  width: 26px;
  height: 26px;
  margin-right: 10px;
}

.admin-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--primary);
  vertical-align: middle;
}

.left, .right {
  flex: 1;
}

.right {
  display: flex;
  justify-content: flex-end;
}

.middle {
  flex: 1;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.circle-icon {
  border-radius: 100%;
  height: 32px;
  width: 32px;
  justify-content: center;
  align-items: center;
  display: flex;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.circle-icon:hover {
  background-color: rgba(161, 59, 110, 0.1);
}

.circle-icon.active {
  background-color: var(--primary);
}

.circle-icon.active .b-icon {
  color: white !important;
}

.b-icon {
  height: 20px;
  width: 20px;
  color: var(--primary) !important;
}

.flex {
  display: flex;
}

/* Override pour les boutons primary - icônes blanches */
.btn-primary .b-icon {
  color: white !important;
}

/* Override pour les boutons avec états actifs */
.circle-icon.active .b-icon {
  color: white !important;
}

/* ============= CONTAINER ============= */
.admin-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 30px 20px;
  position: relative;
}

/* ============= LOADING STATE ============= */
.loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: var(--primary);
  z-index: 1001;
  background-color: rgba(255, 255, 255, 0.9);
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.top-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 30px;
}

/* ============= ADMIN SECTIONS ============= */
.admin-section {
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 2px solid var(--light);
  padding-bottom: 15px;
}

.section-header h3 {
  margin: 0;
  color: var(--primary);
  font-weight: 600;
}

/* ============= FAVORITES SECTION ============= */
.favorites-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 15px;
}

.draggable-grid {
  display: contents;
}

.favorite-card {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  cursor: move;
  transition: transform 0.3s ease;
}

.favorite-card:hover {
  transform: scale(1.05);
}

.favorite-image {
  width: 100%;
  height: 120px;
  object-fit: cover;
}

.favorite-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0,0,0,0.8));
  padding: 10px;
  color: white;
}

.favorite-overlay h6 {
  margin: 0 0 5px 0;
  font-size: 0.85rem;
  line-height: 1.2;
}

/* ============= CATEGORIES SECTION ============= */
.categories-list {
  max-height: 400px;
  overflow-y: auto;
}

.category-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  margin-bottom: 10px;
  background: var(--light);
  border-radius: 8px;
  transition: background-color 0.3s ease;
}

.category-item:hover {
  background: var(--secondary);
}

.category-name {
  font-weight: 500;
  color: var(--primary);
}

.category-actions {
  display: flex;
  gap: 2px;
}

/* ============= RECIPES SECTION ============= */
.recipes-section {
  grid-column: 1 / -1;
}

.recipes-controls {
  display: flex;
  gap: 15px;
  align-items: center;
}

.recipes-table {
  margin-top: 20px;
}

.desktop-table {
  display: block;
}

.mobile-cards {
  display: none;
}

.recipe-thumb {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
}

.category-badge {
  display: inline-block;
  background: var(--primary);
  color: white;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  margin-right: 5px;
  margin-bottom: 3px;
}

/* Mobile Recipe Cards */
.recipe-mobile-card {
  background: white;
  border: 1px solid #e8ecef;
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.mobile-card-header {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 10px;
}

.mobile-recipe-thumb {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 10px;
  flex-shrink: 0;
}

.mobile-card-info {
  flex: 1;
  min-width: 0;
}

.mobile-recipe-name {
  margin: 0 0 5px 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--primary);
  line-height: 1.2;
}

.mobile-recipe-date {
  color: #666;
  font-size: 0.85rem;
}

.mobile-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
}

.mobile-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.mobile-badge {
  font-size: 0.7rem;
  padding: 2px 6px;
}

/* ============= RICH TEXT EDITOR ============= */
.rich-editor {
  border: 2px solid var(--light);
  border-radius: 8px;
  overflow: hidden;
}

.editor-toolbar {
  background: var(--light);
  padding: 10px;
  border-bottom: 2px solid var(--secondary);
}

.content-editor {
  min-height: 200px;
  max-height: 400px;
  padding: 15px;
  outline: none;
  overflow-y: auto;
  border: 1px solid #ddd;
  background: white;
}

.content-editor:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 0.2rem rgba(78, 115, 223, 0.25);
}

/* ============= FORM ELEMENTS ============= */
.photo-preview {
  max-width: 200px;
  max-height: 150px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid var(--secondary);
}

.photo-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: var(--primary);
  opacity: 0.7;
}

/* ============= RECIPE MODAL ============= */
.recipe-modal .modal-dialog {
  max-width: 95vw !important;
  width: 95vw !important;
  margin: 2vh auto !important;
}

.recipe-modal .modal-content {
  height: 96vh !important;
  max-height: 96vh !important;
}

.recipe-modal-body {
  padding: 0 !important;
  height: calc(96vh - 120px) !important;
  overflow: hidden !important;
}

.recipe-tabs {
  height: 100%;
}

.recipe-tabs .tab-content {
  height: calc(100% - 50px);
  overflow-y: auto;
  padding: 20px;
}

.recipe-tabs .nav-tabs {
  border-bottom: 2px solid var(--primary);
  background-color: var(--light);
  margin: 0;
  padding: 0 20px;
}

.recipe-tabs .nav-tabs .nav-link {
  color: var(--primary);
  font-weight: 500;
  border: none;
  border-radius: 0;
  padding: 15px 25px;
}

.recipe-tabs .nav-tabs .nav-link.active {
  background-color: var(--primary);
  color: white;
  border: none;
}

/* ============= RECIPE PREVIEW ============= */
.recipe-preview {
  max-width: 800px;
  margin: 0 auto;
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
}

.preview-header {
  text-align: center;
  margin-bottom: 30px;
  border-bottom: 2px solid var(--light);
  padding-bottom: 20px;
}

.recipe-title {
  color: var(--primary);
  font-size: 2.2rem;
  font-weight: 600;
  margin-bottom: 15px;
  line-height: 1.2;
}

.recipe-meta {
  color: #666;
}

.publication-date {
  font-size: 1.1rem;
  margin-bottom: 15px;
  display: inline-block;
}

.recipe-categories {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
}

.preview-badge {
  background: var(--primary);
  color: white;
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
}

.recipe-photo-container {
  text-align: center;
  margin: 30px 0;
}

.recipe-photo-preview {
  max-width: 100%;
  max-height: 400px;
  object-fit: cover;
  border-radius: 15px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.2);
}

.recipe-content-preview {
  align-self:baseline; 
  padding-top:1rem; 
  padding-bottom:1rem;
  text-align: justify;
}

/* ============= RESPONSIVE ============= */
@media (max-width: 1200px) {
  .top-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .admin-header {
    gap: 15px;
    text-align: center;
  }
  
  .admin-header h1 {
    font-size: 1.5rem;
  }
  
  .admin-container {
    padding: 20px 15px;
  }
  
  .section-header {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }
  
  .favorites-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
  
  /* Switch to mobile cards view */
  .desktop-table {
    display: none;
  }
  
  .mobile-cards {
    display: block;
  }
  
  /* Modal responsive */
  .recipe-modal .modal-dialog {
    max-width: 100vw !important;
    width: 100vw !important;
    margin: 0 !important;
  }
  
  .recipe-modal .modal-content {
    height: 100vh !important;
    border-radius: 0 !important;
  }
  
  .recipe-modal-body {
    height: calc(100vh - 120px) !important;
  }
  
  .recipe-tabs .nav-tabs .nav-link {
    padding: 12px 15px;
    font-size: 0.9rem;
  }
  
  .recipe-preview {
    padding: 20px;
  }
  
  .recipe-title {
    font-size: 1.8rem;
  }
}

@media (max-width: 480px) {
  .recipes-controls {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }
  
  .recipe-tabs .nav-tabs {
    padding: 0 10px;
  }
  
  .recipe-tabs .nav-tabs .nav-link {
    padding: 10px 12px;
    font-size: 0.85rem;
  }
  
  .recipe-preview {
    padding: 15px;
  }
  
  .recipe-title {
    font-size: 1.6rem;
  }
}
</style>